import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api_config';

const FC_PARTNER_ROLES = [
  { value: 'husband', label: 'Husband' }, { value: 'wife', label: 'Wife' },
  { value: 'partner', label: 'Partner' }, { value: 'co_parent', label: 'Co-parent (not together)' }
];

const FamilyOnboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  // Each new page starts at the top.
  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch (e) { window.scrollTo(0, 0); }
  }, [step]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Profile data
  const [profile, setProfile] = useState({
    preferred_name: '',
    roles: [],
    grandparent_role: '',
    family_structure: '',
    challenges: [],
    coach_tone: 'warm',
    faith_preference: '',
    scripture_optin: false,
    partner_role: '',
    partner_name: '',
    household: ''
  });
  
  // Child data
  const [children, setChildren] = useState([]);
  const [existingChildren, setExistingChildren] = useState([]); // Track existing children for deletion
  const [currentChild, setCurrentChild] = useState({
    name: '', age: '', gender: '', relationship: 'biological', challenges: []
  });

  // Load existing profile on mount
  useEffect(() => {
    loadExistingProfile();
  }, []);

  const loadExistingProfile = async () => {
    try {
      const [profileRes, childrenRes] = await Promise.all([
        API.get('/api/v1/family/profile'),
        API.get('/api/v1/family/children')
      ]);
      
      if (profileRes.data) {
        setIsEditMode(true);
        setProfile({
          preferred_name: profileRes.data.preferred_name || '',
          roles: profileRes.data.roles || [],
          grandparent_role: profileRes.data.grandparent_role || '',
          family_structure: profileRes.data.family_structure || '',
          challenges: profileRes.data.challenges || [],
          coach_tone: profileRes.data.coach_tone || 'warm',
          faith_preference: profileRes.data.faith_preference || '',
          scripture_optin: !!profileRes.data.scripture_optin,
          partner_role: profileRes.data.partner_role || '',
          partner_name: profileRes.data.partner_name || '',
          household: profileRes.data.household || ''
        });
      }
      
      if (childrenRes.data && childrenRes.data.length > 0) {
        // Add local id for React keys
        const loadedChildren = childrenRes.data.map(child => ({
          ...child,
          localId: child.id // Keep track of server ID
        }));
        setChildren(loadedChildren);
        setExistingChildren(childrenRes.data.map(c => c.id));
      }
    } catch (err) {
      // No existing profile - that's fine, it's a new setup
      console.log('No existing profile found');
    } finally {
      setInitialLoading(false);
    }
  };

  // Age options with granular infant/toddler stages for ECD support
  const ageOptions = [
    // Infancy (0-12 months)
    { value: '0-3mo', label: 'Newborn (0-3 months)', category: 'infant' },
    { value: '3-6mo', label: 'Infant (3-6 months)', category: 'infant' },
    { value: '6-9mo', label: 'Infant (6-9 months)', category: 'infant' },
    { value: '9-12mo', label: 'Infant (9-12 months)', category: 'infant' },
    // Toddler (12-36 months)
    { value: '12-18mo', label: 'Toddler (12-18 months)', category: 'toddler' },
    { value: '18-24mo', label: 'Toddler (18-24 months)', category: 'toddler' },
    { value: '2yr', label: '2 years old', category: 'toddler' },
    // Preschool (3-5 years)
    { value: '3yr', label: '3 years old', category: 'preschool' },
    { value: '4yr', label: '4 years old', category: 'preschool' },
    { value: '5yr', label: '5 years old', category: 'preschool' },
    // School age (6-12 years)
    { value: '6yr', label: '6 years old', category: 'school' },
    { value: '7yr', label: '7 years old', category: 'school' },
    { value: '8yr', label: '8 years old', category: 'school' },
    { value: '9yr', label: '9 years old', category: 'school' },
    { value: '10yr', label: '10 years old', category: 'school' },
    { value: '11yr', label: '11 years old', category: 'school' },
    { value: '12yr', label: '12 years old', category: 'school' },
    // Teen (13-17 years)
    { value: '13yr', label: '13 years old', category: 'teen' },
    { value: '14yr', label: '14 years old', category: 'teen' },
    { value: '15yr', label: '15 years old', category: 'teen' },
    { value: '16yr', label: '16 years old', category: 'teen' },
    { value: '17yr', label: '17 years old', category: 'teen' },
    // Young Adult (18+)
    { value: '18+', label: '18+ (Adult Child)', category: 'adult' }
  ];

  // Relationship options
  const relationshipOptions = [
    { value: 'biological', label: 'Biological child' },
    { value: 'step-child', label: 'Step-child' },
    { value: 'grandchild', label: 'Grandchild' },
    { value: 'foster', label: 'Foster child' },
    { value: 'adopted', label: 'Adopted child' }
  ];

  // Grandparent role spectrum options
  const grandparentRoleOptions = [
    { 
      value: 'primary_custodian', 
      label: 'Primary Custodian', 
      desc: "I'm raising my grandchildren full-time",
      icon: '🏠'
    },
    { 
      value: 'co_parenting', 
      label: 'Co-Parenting Grandparent', 
      desc: 'I share caregiving responsibilities with their parents',
      icon: '🤝'
    },
    { 
      value: 'active_support', 
      label: 'Active Support', 
      desc: 'I help regularly (weekends, after school, etc.)',
      icon: '💪'
    },
    { 
      value: 'traditional', 
      label: 'Traditional Grandparent', 
      desc: 'I see them for visits and special occasions',
      icon: '💝'
    }
  ];

  // Helper function to get display label from age value
  const getAgeLabel = (ageValue) => {
    if (!ageValue) return '';
    // Handle legacy integer ages
    if (!isNaN(ageValue) && Number.isInteger(Number(ageValue))) {
      return `${ageValue} years old`;
    }
    const option = ageOptions.find(opt => opt.value === ageValue);
    return option ? option.label : ageValue;
  };

  // Helper function to get relationship label
  const getRelationshipLabel = (relValue) => {
    const option = relationshipOptions.find(opt => opt.value === relValue);
    return option ? option.label : relValue;
  };

  const roleOptions = [
    { value: 'parent', label: 'Parent', icon: '👨‍👩‍👧' },
    { value: 'grandparent', label: 'Grandparent', icon: '👴👵' },
    { value: 'step-parent', label: 'Step-Parent', icon: '👨‍👧' },
    { value: 'guardian', label: 'Guardian', icon: '🤝' },
    { value: 'foster', label: 'Foster Parent', icon: '💜' }
  ];

  const structureOptions = [
    { value: 'nuclear', label: 'Two-Parent Household', desc: 'Both parents in the home' },
    { value: 'single_parent', label: 'Single Parent', desc: 'One parent household' },
    { value: 'blended', label: 'Blended Family', desc: 'Step-parents/step-siblings' },
    { value: 'grandparent_led', label: 'Grandparent-Led', desc: 'Grandparents as primary caregivers' },
    { value: 'co_parenting', label: 'Co-Parenting', desc: 'Shared custody between homes' }
  ];

  const challengeOptions = [
    'Behavior issues', 'Screen time', 'Sleep problems', 'Sibling rivalry',
    'Communication', 'Discipline consistency', 'Anxiety/stress', 'School issues',
    'Blended family dynamics', 'Co-parenting communication', 'Teen independence'
  ];

  const childChallengeOptions = [
    'Tantrums/meltdowns', 'Defiance', 'Anxiety', 'ADHD', 'Autism spectrum',
    'Learning differences', 'Social skills', 'Emotional regulation', 'Sleep issues'
  ];

  const toggleRole = (role) => {
    setProfile(prev => {
      const newRoles = prev.roles.includes(role) 
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role];
      
      // Clear grandparent_role if grandparent is deselected
      const newGrandparentRole = newRoles.includes('grandparent') ? prev.grandparent_role : '';
      
      return {
        ...prev,
        roles: newRoles,
        grandparent_role: newGrandparentRole
      };
    });
  };

  const toggleChallenge = (challenge) => {
    setProfile(prev => ({
      ...prev,
      challenges: prev.challenges.includes(challenge)
        ? prev.challenges.filter(c => c !== challenge)
        : [...prev.challenges, challenge]
    }));
  };

  const toggleChildChallenge = (challenge) => {
    setCurrentChild(prev => ({
      ...prev,
      challenges: prev.challenges.includes(challenge)
        ? prev.challenges.filter(c => c !== challenge)
        : [...prev.challenges, challenge]
    }));
  };

  const addChild = () => {
    if (currentChild.name && currentChild.age) {
      setChildren([...children, { ...currentChild, id: Date.now(), isNew: true }]);
      setCurrentChild({ name: '', age: '', gender: '', relationship: 'biological', challenges: [] });
    }
  };

  const removeChild = async (childId, isExisting) => {
    if (isExisting) {
      // Delete from server
      try {
        await API.delete(`/api/v1/family/children/${childId}`);
      } catch (err) {
        console.error('Failed to delete child:', err);
      }
    }
    setChildren(children.filter(c => (c.id || c.localId) !== childId));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Create/update family profile
      await API.post('/api/v1/family/profile', {
        ...profile,
        grandparent_role: profile.grandparent_role || null
      });
      
      // Add new children only
      for (const child of children) {
        if (child.isNew) {
          await API.post('/api/v1/family/children', {
            name: child.name,
            age: child.age,
            gender: child.gender || null,
            relationship: child.relationship,
            challenges: child.challenges
          });
        }
      }
      
      // Navigate to Family Coach chat
      navigate('/family-coach');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setIsLoading(false);
    }
  };

  const cardStyle = {
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  };

  const selectedCardStyle = {
    ...cardStyle,
    background: 'rgba(74, 144, 226, 0.2)',
    border: '2px solid #4A90E2'
  };

  const chipStyle = (selected) => ({
    padding: '8px 16px',
    borderRadius: '20px',
    border: selected ? '2px solid #4A90E2' : '1px solid rgba(255, 255, 255, 0.2)',
    background: selected ? 'rgba(74, 144, 226, 0.2)' : 'transparent',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s'
  });

  const selectStyle = {
    width: '100%',
    padding: '12px',
    background: '#2a2a4a',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    color: 'white',
    fontSize: '14px',
    cursor: 'pointer'
  };

  const optionStyle = {
    background: '#2a2a4a',
    color: 'white'
  };

  // Check if grandparent is selected
  const isGrandparentSelected = profile.roles.includes('grandparent');

  // Show loading state while checking for existing profile
  if (initialLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ color: 'white', fontSize: '18px' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '40px 20px',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif"
    }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>👨‍👩‍👧‍👦</div>
          <h1 style={{ color: 'white', fontSize: '28px', marginBottom: '4px' }}>Family Coach</h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '16px' }}>
            {isEditMode ? 'Update your family profile' : "Let's personalize your experience"}
          </p>
          
          {/* Progress */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
            {[1, 2, 3, 4, 5].map(s => (
              <div key={s} style={{
                width: '60px', height: '4px', borderRadius: '2px',
                background: s <= step ? '#4A90E2' : 'rgba(255,255,255,0.2)'
              }} />
            ))}
          </div>
        </div>

        {error && (
          <div style={{
            padding: '16px', background: 'rgba(231, 76, 60, 0.1)',
            border: '1px solid rgba(231, 76, 60, 0.3)', borderRadius: '12px',
            marginBottom: '24px', color: '#e74c3c', textAlign: 'center'
          }}>{error}</div>
        )}

        {/* Step 1: Name & Roles */}
        {step === 1 && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '24px' }}>What should we call you?</h2>
            <input
              type="text"
              placeholder="Your first name"
              value={profile.preferred_name}
              onChange={(e) => setProfile({ ...profile, preferred_name: e.target.value })}
              style={{
                width: '100%', padding: '16px', fontSize: '16px',
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px', color: 'white', marginBottom: '32px', boxSizing: 'border-box'
              }}
            />
            
            <h2 style={{ color: 'white', marginBottom: '16px' }}>What's your role?</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '20px', fontSize: '14px' }}>
              Select all that apply
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {roleOptions.map(role => (
                <div
                  key={role.value}
                  onClick={() => toggleRole(role.value)}
                  style={profile.roles.includes(role.value) ? selectedCardStyle : cardStyle}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>{role.icon}</div>
                  <div style={{ color: 'white', fontWeight: '600' }}>{role.label}</div>
                </div>
              ))}
            </div>

            {/* Grandparent Role Spectrum */}
            {isGrandparentSelected && (
              <div style={{ 
                marginTop: '32px', 
                padding: '24px', 
                background: 'rgba(255,255,255,0.03)', 
                borderRadius: '16px',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                <h3 style={{ color: 'white', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  👴👵 How would you describe your grandparent role?
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '20px', fontSize: '14px' }}>
                  This helps us provide guidance tailored to your involvement level
                </p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {grandparentRoleOptions.map(option => (
                    <div
                      key={option.value}
                      onClick={() => setProfile({ ...profile, grandparent_role: option.value })}
                      style={{
                        ...cardStyle,
                        padding: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        ...(profile.grandparent_role === option.value ? {
                          background: 'rgba(74, 144, 226, 0.2)',
                          border: '2px solid #4A90E2'
                        } : {})
                      }}
                    >
                      <div style={{ fontSize: '24px' }}>{option.icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: 'white', fontWeight: '600', marginBottom: '2px' }}>
                          {option.label}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                          {option.desc}
                        </div>
                      </div>
                      {profile.grandparent_role === option.value && (
                        <div style={{ color: '#4A90E2', fontSize: '20px' }}>✓</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Family Structure */}
        {step === 2 && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '24px' }}>Your family structure</h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {structureOptions.map(opt => (
                <div
                  key={opt.value}
                  onClick={() => setProfile({ ...profile, family_structure: opt.value })}
                  style={profile.family_structure === opt.value ? selectedCardStyle : cardStyle}
                >
                  <div style={{ color: 'white', fontWeight: '600', marginBottom: '4px' }}>{opt.label}</div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>{opt.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '36px' }}>
              <h3 style={{ color: 'white', margin: '0 0 6px', fontSize: '19px' }}>Your spouse or partner (optional)</h3>
              <p style={{ color: 'rgba(255,255,255,0.75)', margin: '0 0 14px', fontSize: '15px' }}>
                So your coach can talk about them by name.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                {FC_PARTNER_ROLES.map(r => (
                  <button key={r.value} type="button" aria-pressed={profile.partner_role === r.value}
                    onClick={() => setProfile({ ...profile, partner_role: profile.partner_role === r.value ? '' : r.value })}
                    style={{ ...chipStyle(profile.partner_role === r.value), fontSize: '15px', padding: '10px 16px', fontFamily: 'inherit' }}>
                    {profile.partner_role === r.value ? '\u2713 ' : ''}{r.label}
                  </button>
                ))}
              </div>
              <input type="text" value={profile.partner_name} maxLength={30} placeholder="Their first name"
                aria-label="Spouse or partner's first name"
                onChange={(e) => setProfile({ ...profile, partner_name: e.target.value })}
                style={{ width: '100%', padding: '14px 16px', fontSize: '16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', color: 'white', boxSizing: 'border-box' }} />
              <h3 style={{ color: 'white', margin: '28px 0 6px', fontSize: '19px' }}>Anyone else at home or close to your family? (optional)</h3>
              <input type="text" value={profile.household} maxLength={120} placeholder="For example: my mother lives with us"
                aria-label="Anyone else at home or close to your family"
                onChange={(e) => setProfile({ ...profile, household: e.target.value })}
                style={{ width: '100%', padding: '14px 16px', fontSize: '16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', color: 'white', boxSizing: 'border-box' }} />
            </div>
          </div>
        )}

        {/* Step 3: Children */}
        {step === 3 && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '24px' }}>
              {isEditMode ? 'Your children' : 'Tell us about your children'}
            </h2>
            
            {/* Existing children */}
            {children.map(child => (
              <div key={child.id || child.localId} style={{
                ...cardStyle,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: '12px',
                background: child.isNew ? 'rgba(74, 144, 226, 0.1)' : 'rgba(255,255,255,0.05)'
              }}>
                <div>
                  <div style={{ color: 'white', fontWeight: '600' }}>
                    {child.name}
                    {child.isNew && <span style={{ color: '#4A90E2', fontSize: '12px', marginLeft: '8px' }}>(new)</span>}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                    {getAgeLabel(child.age)} • {getRelationshipLabel(child.relationship)}
                  </div>
                </div>
                <button 
                  onClick={() => removeChild(child.id || child.localId, !child.isNew)} 
                  style={{
                    background: 'rgba(231,76,60,0.2)', border: 'none', color: '#e74c3c',
                    padding: '8px 12px', borderRadius: '8px', cursor: 'pointer'
                  }}
                >Remove</button>
              </div>
            ))}
            
            {/* Add child form */}
            <div style={{ ...cardStyle, marginTop: '20px' }}>
              <h4 style={{ color: 'white', marginBottom: '16px', marginTop: 0 }}>Add a child</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <input
                  placeholder="Child's name"
                  value={currentChild.name}
                  onChange={(e) => setCurrentChild({ ...currentChild, name: e.target.value })}
                  style={{
                    padding: '12px', background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: 'white'
                  }}
                />
                <select
                  value={currentChild.age}
                  onChange={(e) => setCurrentChild({ ...currentChild, age: e.target.value })}
                  style={selectStyle}
                >
                  <option value="" disabled style={optionStyle}>Select age</option>
                  <optgroup label="Infant (0-12 months)" style={optionStyle}>
                    <option value="0-3mo" style={optionStyle}>Newborn (0-3 months)</option>
                    <option value="3-6mo" style={optionStyle}>Infant (3-6 months)</option>
                    <option value="6-9mo" style={optionStyle}>Infant (6-9 months)</option>
                    <option value="9-12mo" style={optionStyle}>Infant (9-12 months)</option>
                  </optgroup>
                  <optgroup label="Toddler (1-2 years)" style={optionStyle}>
                    <option value="12-18mo" style={optionStyle}>Toddler (12-18 months)</option>
                    <option value="18-24mo" style={optionStyle}>Toddler (18-24 months)</option>
                    <option value="2yr" style={optionStyle}>2 years old</option>
                  </optgroup>
                  <optgroup label="Preschool (3-5 years)" style={optionStyle}>
                    <option value="3yr" style={optionStyle}>3 years old</option>
                    <option value="4yr" style={optionStyle}>4 years old</option>
                    <option value="5yr" style={optionStyle}>5 years old</option>
                  </optgroup>
                  <optgroup label="School Age (6-12 years)" style={optionStyle}>
                    <option value="6yr" style={optionStyle}>6 years old</option>
                    <option value="7yr" style={optionStyle}>7 years old</option>
                    <option value="8yr" style={optionStyle}>8 years old</option>
                    <option value="9yr" style={optionStyle}>9 years old</option>
                    <option value="10yr" style={optionStyle}>10 years old</option>
                    <option value="11yr" style={optionStyle}>11 years old</option>
                    <option value="12yr" style={optionStyle}>12 years old</option>
                  </optgroup>
                  <optgroup label="Teen (13-17 years)" style={optionStyle}>
                    <option value="13yr" style={optionStyle}>13 years old</option>
                    <option value="14yr" style={optionStyle}>14 years old</option>
                    <option value="15yr" style={optionStyle}>15 years old</option>
                    <option value="16yr" style={optionStyle}>16 years old</option>
                    <option value="17yr" style={optionStyle}>17 years old</option>
                  </optgroup>
                  <optgroup label="Adult" style={optionStyle}>
                    <option value="18+" style={optionStyle}>18+ (Adult Child)</option>
                  </optgroup>
                </select>
              </div>
              
              <select
                value={currentChild.relationship}
                onChange={(e) => setCurrentChild({ ...currentChild, relationship: e.target.value })}
                style={{ ...selectStyle, marginBottom: '12px' }}
              >
                <option value="biological" style={optionStyle}>Biological child</option>
                <option value="step-child" style={optionStyle}>Step-child</option>
                <option value="grandchild" style={optionStyle}>Grandchild</option>
                <option value="foster" style={optionStyle}>Foster child</option>
                <option value="adopted" style={optionStyle}>Adopted child</option>
              </select>
              
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', marginBottom: '8px' }}>
                Any challenges? (optional)
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                {childChallengeOptions.map(ch => (
                  <span
                    key={ch}
                    onClick={() => toggleChildChallenge(ch)}
                    style={chipStyle(currentChild.challenges.includes(ch))}
                  >{ch}</span>
                ))}
              </div>
              
              <button
                onClick={addChild}
                disabled={!currentChild.name || !currentChild.age}
                style={{
                  width: '100%', padding: '12px', background: '#4A90E2',
                  border: 'none', borderRadius: '8px', color: 'white',
                  fontWeight: '600', cursor: currentChild.name && currentChild.age ? 'pointer' : 'not-allowed',
                  opacity: currentChild.name && currentChild.age ? 1 : 0.5
                }}
              >+ Add Child</button>
            </div>
          </div>
        )}

        {/* Step 4: Challenges */}
        {step === 4 && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '16px' }}>What brings you here?</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '24px', fontSize: '14px' }}>
              Select any challenges you'd like help with
            </p>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {challengeOptions.map(ch => (
                <span
                  key={ch}
                  onClick={() => toggleChallenge(ch)}
                  style={chipStyle(profile.challenges.includes(ch))}
                >{ch}</span>
              ))}
            </div>
          </div>
        )}

        {/* Step 5: Faith Preference */}
        {step === 5 && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '16px' }}>Faith & Guidance Style</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '24px', fontSize: '14px' }}>
              This helps us personalize your experience
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div onClick={() => setProfile({...profile, faith_preference: 'christian', scripture_optin: false})} style={{ padding: '20px', background: profile.faith_preference === 'christian' ? 'rgba(74,144,226,0.3)' : 'rgba(255,255,255,0.05)', border: profile.faith_preference === 'christian' ? '2px solid #4A90E2' : '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '28px' }}>✝️</span>
                  <div>
                    <div style={{ color: 'white', fontWeight: '600', fontSize: '16px' }}>Christian</div>
                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>Include biblical wisdom and scripture in guidance</div>
                  </div>
                  {profile.faith_preference === 'christian' && <span style={{ marginLeft: 'auto', color: '#4A90E2' }}>✓</span>}
                </div>
              </div>
              <div onClick={() => setProfile({...profile, faith_preference: 'non_christian'})} style={{ padding: '20px', background: profile.faith_preference === 'non_christian' ? 'rgba(74,144,226,0.3)' : 'rgba(255,255,255,0.05)', border: profile.faith_preference === 'non_christian' ? '2px solid #4A90E2' : '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '28px' }}>🌱</span>
                  <div>
                    <div style={{ color: 'white', fontWeight: '600', fontSize: '16px' }}>Secular Guidance</div>
                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>Practical wisdom with no Scripture, prayer or religious instruction</div>
                  </div>
                  {profile.faith_preference === 'non_christian' && <span style={{ marginLeft: 'auto', color: '#4A90E2' }}>✓</span>}
                </div>
              </div>
              {profile.faith_preference === 'non_christian' && (
                <div style={{ padding: '16px 18px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)' }}>
                  <div style={{ color: 'white', fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>Would you like related Scripture offered at the end of replies?</div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginBottom: '12px' }}>Optional. You can change this any time in your profile.</div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {[[false, 'No, thanks'], [true, 'Yes']].map(([val, label]) => (
                      <button key={label} type="button" onClick={() => setProfile({...profile, scripture_optin: val})}
                        style={{ padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit', color: 'white',
                          border: profile.scripture_optin === val ? '1px solid #4A90E2' : '1px solid rgba(255,255,255,0.2)',
                          background: profile.scripture_optin === val ? 'rgba(74,144,226,0.3)' : 'transparent' }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '16px', textAlign: 'center' }}>You can change this anytime in your profile settings</p>
          </div>
        )}

        {/* Navigation Buttons */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '40px' }}>
          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              style={{
                flex: 1, padding: '16px', background: 'transparent',
                border: '1px solid rgba(255,255,255,0.3)', borderRadius: '12px',
                color: 'white', fontSize: '16px', cursor: 'pointer'
              }}
            >Back</button>
          )}
          
          {step < 5 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && profile.roles.length === 0}
              style={{
                flex: 1, padding: '16px',
                background: 'linear-gradient(135deg, #4A90E2, #6CB4EE)',
                border: 'none', borderRadius: '12px', color: 'white',
                fontSize: '16px', fontWeight: '600', cursor: 'pointer'
              }}
            >Continue</button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              style={{
                flex: 1, padding: '16px',
                background: 'linear-gradient(135deg, #50C878, #3CB371)',
                border: 'none', borderRadius: '12px', color: 'white',
                fontSize: '16px', fontWeight: '600', cursor: 'pointer'
              }}
            >{isLoading ? 'Saving...' : (isEditMode ? 'Save Changes →' : 'Start Family Coach →')}</button>
          )}
        </div>
        
        {/* Cancel/Skip option */}
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            onClick={() => navigate('/family-coach')}
            style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer', fontSize: '14px'
            }}
          >{isEditMode ? 'Cancel' : 'Skip for now'}</button>
        </div>
      </div>
    </div>
  );
};

export default FamilyOnboarding;
